import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Loader2, CreditCard, CheckCircle, Clock, ExternalLink, AlertTriangle,
  Search, ChevronLeft, ChevronRight, Copy, ChevronDown, ChevronUp,
  Webhook, CheckCircle2, XCircle, X, HelpCircle, TrendingUp, Tag
} from 'lucide-react';

// ── Summary Card ────────────────────────────────────────
const SummaryCard = ({ label, count, total, color, icon: Icon, testId }) => (
  <Card className="border-border bg-card" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
        <span className={`p-1.5 rounded-md ${color.bg}`}><Icon className={`h-3.5 w-3.5 ${color.icon}`} /></span>
      </div>
      <p className={`text-2xl font-bold ${color.text}`}>{count}</p>
      <p className="text-xs text-muted-foreground mt-0.5">Total: {total}</p>
    </CardContent>
  </Card>
);

// ── Callback Detail Modal ───────────────────────────────
const CallbackDetailModal = ({ callback, onClose }) => {
  if (!callback) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[hsl(var(--card))] border border-border rounded-xl p-5 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="callback-detail-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Webhook className="h-4 w-4" /> Webhook Callback Details
          </h3>
          <button onClick={onClose}><X className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20">Status</span>
            <Badge className={callback.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
              {callback.success ? 'Delivered' : 'Failed'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20">Event</span>
            <span className="text-xs font-mono text-foreground">{callback.event || '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20">HTTP Code</span>
            <span className={`text-xs font-mono ${callback.status_code >= 200 && callback.status_code < 300 ? 'text-emerald-400' : 'text-red-400'}`}>
              {callback.status_code || '—'}
            </span>
          </div>
          {callback.url && (
            <div>
              <span className="text-xs text-muted-foreground block mb-1">URL</span>
              <p className="text-xs font-mono text-foreground bg-muted/30 p-2 rounded break-all">{callback.url}</p>
            </div>
          )}
          {callback.response && (
            <div>
              <span className="text-xs text-muted-foreground block mb-1">Response</span>
              <p className="text-xs font-mono text-muted-foreground bg-muted/30 p-2 rounded break-all max-h-24 overflow-y-auto">{callback.response}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-20">Attempt</span>
            <span className="text-xs text-foreground">{callback.attempt || '—'}</span>
          </div>
          {callback.timestamp && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-20">Sent At</span>
              <span className="text-xs text-foreground">{new Date(callback.timestamp).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Callback Status Icon ────────────────────────────────
const CallbackIcon = ({ callback, onClick }) => {
  if (!callback) {
    return (
      <button title="No callback sent" className="text-muted-foreground/40 cursor-default" data-testid="callback-none">
        <HelpCircle className="h-4 w-4" />
      </button>
    );
  }
  if (callback.success) {
    return (
      <button onClick={onClick} title="Callback delivered — click for details" className="text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="callback-success">
        <CheckCircle2 className="h-4 w-4" />
      </button>
    );
  }
  return (
    <button onClick={onClick} title="Callback failed — click for details" className="text-red-400 hover:text-red-300 transition-colors" data-testid="callback-failed">
      <XCircle className="h-4 w-4" />
    </button>
  );
};

// ── Main Component ──────────────────────────────────────
const MerchantPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ search: '', from_date: '', till_date: '' });
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [callbackDetail, setCallbackDetail] = useState(null);
  const [stats, setStats] = useState(null);

  const LIMIT = 100;

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payments/summary-stats`);
      setStats(res.data);
    } catch { /* non-critical */ }
  }, []);

  const fetchPayments = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filters.search) params.append('search', filters.search);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.till_date) params.append('till_date', filters.till_date);
      const res = await axios.get(`${API}/payments?${params}`);
      setPayments(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch { toast.error('Failed to fetch payments'); }
  }, [filters]);

  useEffect(() => { setLoading(true); fetchPayments(1).finally(() => setLoading(false)); }, [fetchPayments]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => fetchPayments(pagination.page), 5000);
    return () => clearInterval(pollingRef.current);
  }, [fetchPayments, pagination.page]);

  // Group payments by invoice_id
  const groupedPayments = useMemo(() => {
    const groups = {};
    const ungrouped = [];
    for (const p of payments) {
      const iid = p.invoice_id;
      if (iid && p.invoice_payment_count > 1) {
        if (!groups[iid]) groups[iid] = { invoiceId: iid, payments: [], total: p.invoice_total, totalReceived: p.invoice_total_received, fullyPaid: p.invoice_fully_paid };
        groups[iid].payments.push(p);
      } else {
        ungrouped.push({ type: 'single', payment: p });
      }
    }
    const result = [];
    const addedGroups = new Set();
    for (const p of payments) {
      const iid = p.invoice_id;
      if (iid && groups[iid] && !addedGroups.has(iid)) {
        result.push({ type: 'group', ...groups[iid] });
        addedGroups.add(iid);
      } else if (!groups[iid]) {
        result.push({ type: 'single', payment: p });
      }
    }
    return result;
  }, [payments]);

  const toggleGroup = (invoiceId) => setExpandedInvoices(prev => ({ ...prev, [invoiceId]: !prev[invoiceId] }));

  const handleSearch = (e) => { e.preventDefault(); fetchPayments(1); };
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const copyToClipboard = (text) => { if (text && text !== '—') { navigator.clipboard.writeText(text); toast.success('Copied'); } };

  const getStatusBadge = (status, payment) => {
    // Multi-payment invoice: some payments received but invoice not fully paid
    if (status === 'confirmed' && payment.invoice_payment_count > 0 && !payment.invoice_fully_paid) {
      return <Badge className="bg-amber-500/20 text-amber-500"><AlertTriangle className="h-3 w-3 mr-1" />Partially Paid</Badge>;
    }
    if (status === 'confirmed') {
      // Check if received amount is less than invoice amount (within-tolerance underpayment)
      const invoiceAmt = parseFloat(payment.invoice_amount || payment.invoice_total || 0);
      const receivedAmt = parseFloat(payment.amount || 0);
      if (invoiceAmt > 0 && receivedAmt < invoiceAmt) {
        return <Badge className="bg-amber-500/20 text-amber-400"><AlertTriangle className="h-3 w-3 mr-1" />Under Paid</Badge>;
      }
      return <Badge className="bg-emerald-500/20 text-emerald-500"><CheckCircle className="h-3 w-3 mr-1" />Confirmed</Badge>;
    }
    if (status === 'underpaid') return <Badge className="bg-amber-500/20 text-amber-500"><AlertTriangle className="h-3 w-3 mr-1" />Underpaid</Badge>;
    return <Badge className="bg-yellow-500/20 text-yellow-500"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  const getExplorerUrl = (txHash) => {
    if (!txHash) return '#';
    if (txHash.startsWith('0x')) return `https://bscscan.com/tx/${txHash}`;
    return `https://tronscan.org/#/transaction/${txHash}`;
  };

  const truncate = (str, start = 6, end = 4) => str ? `${str.slice(0, start)}...${str.slice(-end)}` : '—';

  const renderPaymentRow = (p, isChild = false, groupColor = null) => (
    <TableRow key={p.id} className={isChild ? `border-l-2 ${groupColor === 'green' ? 'bg-emerald-500/5 border-l-emerald-500' : 'bg-red-500/5 border-l-red-500'}` : ''} data-testid={`payment-row-${p.id}`}>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
        {p.invoice_reference ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help border-b border-dashed border-muted-foreground/40">
                {fmtDate(p.detected_at)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[240px]">
              <div className="flex items-center gap-1.5">
                <Tag className="h-3 w-3 shrink-0" />
                <span className="font-medium">Ref:</span>
                <span className="break-all">{p.invoice_reference}</span>
              </div>
            </TooltipContent>
          </Tooltip>
        ) : (
          fmtDate(p.detected_at)
        )}
      </TableCell>
      <TableCell>
        <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors"
          onClick={() => copyToClipboard(p.invoice_id)} title={p.invoice_id}>
          {truncate(p.invoice_id, 8, 4)} <Copy className="h-3 w-3 inline opacity-40" />
        </span>
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {p.invoice_amount || p.invoice_total || '—'} <span className="text-muted-foreground text-xs">{p.currency}</span>
      </TableCell>
      <TableCell className="text-right font-mono text-sm font-semibold">
        {p.amount} <span className="text-muted-foreground text-xs">{p.currency}</span>
      </TableCell>
      <TableCell>{getStatusBadge(p.status, p)}</TableCell>
      <TableCell className="text-center">
        <CallbackIcon callback={p.callback} onClick={() => p.callback && setCallbackDetail(p.callback)} />
      </TableCell>
      <TableCell>
        {p.to_address ? (
          <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(p.to_address)} title={p.to_address}>
            {truncate(p.to_address)} <Copy className="h-3 w-3 inline opacity-40" />
          </span>
        ) : '—'}
      </TableCell>
      <TableCell>
        {p.from_address ? (
          <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(p.from_address)} title={p.from_address}>
            {truncate(p.from_address)} <Copy className="h-3 w-3 inline opacity-40" />
          </span>
        ) : '—'}
      </TableCell>
      <TableCell>
        {p.tx_hash ? (
          <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(p.tx_hash)} title={p.tx_hash}>
            {truncate(p.tx_hash, 10, 4)} <Copy className="h-3 w-3 inline opacity-40" />
          </span>
        ) : '—'}
      </TableCell>
      <TableCell className="text-right">
        {p.tx_hash && (
          <a href={getExplorerUrl(p.tx_hash)} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" title="Check on blockchain" data-testid={`check-payment-${p.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Verify
            </Button>
          </a>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <MerchantLayout title="Payments">
      <TooltipProvider>
      <div className="space-y-6" data-testid="payments-page">
        <p className="text-muted-foreground">View all verified crypto payments</p>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payment-summary-cards">
          <SummaryCard
            label="Total Payments"
            count={stats?.total?.count ?? '—'}
            total={`${stats?.total?.total ?? '—'} crypto`}
            color={{ bg: 'bg-indigo-500/10', icon: 'text-indigo-400', text: 'text-foreground' }}
            icon={TrendingUp}
            testId="stat-total-payments"
          />
          <SummaryCard
            label="Full Payments"
            count={stats?.full?.count ?? '—'}
            total={`${stats?.full?.total ?? '—'} crypto`}
            color={{ bg: 'bg-emerald-500/10', icon: 'text-emerald-400', text: 'text-emerald-400' }}
            icon={CheckCircle}
            testId="stat-full-payments"
          />
          <SummaryCard
            label="Underpaid"
            count={stats?.underpaid?.count ?? '—'}
            total={`${stats?.underpaid?.total ?? '—'} crypto`}
            color={{ bg: 'bg-amber-500/10', icon: 'text-amber-400', text: 'text-amber-400' }}
            icon={AlertTriangle}
            testId="stat-underpaid"
          />
          <SummaryCard
            label="Overpaid"
            count={stats?.overpaid?.count ?? '—'}
            total={`${stats?.overpaid?.total ?? '—'} crypto`}
            color={{ bg: 'bg-blue-500/10', icon: 'text-blue-400', text: 'text-blue-400' }}
            icon={CreditCard}
            testId="stat-overpaid"
          />
        </div>

        {/* Search & Filters */}
        <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3" data-testid="payment-filters">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Amount, Invoice ID, Wallet, TXN..." className="pl-9" value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="payment-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">From Date</Label>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters(f => ({ ...f, from_date: e.target.value }))} className="w-[160px]" data-testid="payment-from-date" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Till Date</Label>
            <Input type="date" value={filters.till_date} onChange={(e) => setFilters(f => ({ ...f, till_date: e.target.value }))} className="w-[160px]" data-testid="payment-till-date" />
          </div>
          <Button type="submit" size="sm" className="bg-primary text-white" data-testid="payment-search-btn">
            <Search className="h-4 w-4 mr-1" /> Search
          </Button>
          {(filters.search || filters.from_date || filters.till_date) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setFilters({ search: '', from_date: '', till_date: '' })} data-testid="payment-clear-filters">Clear</Button>
          )}
        </form>

        {/* Data Table */}
        <Card className="border-border bg-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : payments.length === 0 ? (
              <div className="py-12 text-center">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-base font-semibold mb-2">No Payments Found</h3>
                <p className="text-muted-foreground text-sm">Payments will appear here once customers pay your invoices</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[160px]">Date & Time</TableHead>
                        <TableHead>Invoice ID</TableHead>
                        <TableHead className="text-right">Invoice Amount</TableHead>
                        <TableHead className="text-right">Received Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-center w-[50px]" title="Webhook Callback Status">
                          <Webhook className="h-3.5 w-3.5 mx-auto" />
                        </TableHead>
                        <TableHead>Received Wallet</TableHead>
                        <TableHead>Sender Wallet</TableHead>
                        <TableHead>TXN ID</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupedPayments.map((item) => {
                        if (item.type === 'single') return renderPaymentRow(item.payment);

                        const expanded = expandedInvoices[item.invoiceId];
                        const firstPayment = item.payments[0];

                        return (
                          <React.Fragment key={`grp-${item.invoiceId}`}>
                            {/* Group header row */}
                            <TableRow
                              className={`cursor-pointer transition-colors ${item.fullyPaid ? 'bg-emerald-500/5 hover:bg-emerald-500/10 border-l-2 border-l-emerald-500' : 'bg-red-500/5 hover:bg-red-500/10 border-l-2 border-l-red-500'}`}
                              onClick={() => toggleGroup(item.invoiceId)}
                              data-testid={`payment-group-${item.invoiceId}`}
                            >
                              <TableCell colSpan={2}>
                                <div className="flex items-center gap-2">
                                  {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                                  <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary"
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(item.invoiceId); }} title={item.invoiceId}>
                                    {truncate(item.invoiceId, 8, 4)} <Copy className="h-3 w-3 inline opacity-40" />
                                  </span>
                                  <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px]">{item.payments.length} payments</Badge>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {item.total || firstPayment.invoice_amount || '—'} <span className="text-muted-foreground text-xs">{firstPayment.currency}</span>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm font-semibold">
                                <span className={item.fullyPaid ? 'text-emerald-400' : 'text-red-400'}>
                                  {item.totalReceived}
                                </span>
                                <span className="text-muted-foreground text-xs ml-1">{firstPayment.currency}</span>
                              </TableCell>
                              <TableCell>
                                {item.fullyPaid
                                  ? <Badge className="bg-emerald-500/20 text-emerald-400"><CheckCircle className="h-3 w-3 mr-1" />Fully Paid</Badge>
                                  : <Badge className="bg-red-500/20 text-red-400"><AlertTriangle className="h-3 w-3 mr-1" />Partial</Badge>
                                }
                              </TableCell>
                              <TableCell colSpan={5} className="text-right text-xs text-muted-foreground">
                                Click to {expanded ? 'collapse' : 'expand'}
                              </TableCell>
                            </TableRow>
                            {/* Child payment rows */}
                            {expanded && item.payments.map(p => renderPaymentRow(p, true, item.fullyPaid ? 'green' : 'red'))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Showing {(pagination.page - 1) * LIMIT + 1}–{Math.min(pagination.page * LIMIT, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={pagination.page <= 1} onClick={() => fetchPayments(pagination.page - 1)} data-testid="payment-prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.pages}</span>
                    <Button size="sm" variant="outline" disabled={pagination.page >= pagination.pages} onClick={() => fetchPayments(pagination.page + 1)} data-testid="payment-next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Callback Detail Modal */}
        {callbackDetail && <CallbackDetailModal callback={callbackDetail} onClose={() => setCallbackDetail(null)} />}
      </div>
      </TooltipProvider>
    </MerchantLayout>
  );
};

export default MerchantPayments;
